import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { LoginService } from '../../auth/login/login.service';
import { PermissionService } from '../../../@backoffice/services/permission.service';
import { UserService } from '../../../@backoffice/services/user.service';
import { PostLoginRedirectService } from '../services/post-login-redirect.service';
import { buildClientPath, buildClientRoute } from '../utils/client-route.util';

/**
 * Authentication Guard
 * 
 * This guard protects routes by checking if the user is authenticated.
 * If not authenticated, it redirects to the login page.
 * 
 * Usage: Add `canActivate: [authGuard]` to any route that requires authentication.
 */
export const authGuard: CanActivateFn = (route, state) => {
  const loginService = inject(LoginService);
  const router = inject(Router);

  // logout() clears redirect_url, so the attempted URL must be stored after it.
  const redirectToLogin = () => {
    loginService.logout();
    loginService.setRedirectUrl(state.url);
    return router.createUrlTree(buildClientRoute(router, ['/login']));
  };

  switch (loginService.getAuthState()) {
    case 'valid':
      return true;

    case 'expired':
      // The access token lapsed but the refresh token is still valid, so renew it here
      // instead of ending the session. The 401 interceptor cannot cover this: guards run
      // before any HTTP request is issued, so no 401 is ever produced (TDA3-T473).
      return loginService.refreshAccessTokenShared().pipe(
        map(() => true),
        catchError(() => {
          console.warn('Session refresh failed at auth guard. Redirecting to login page.');
          return of(redirectToLogin());
        })
      );

    default:
      console.warn('Authentication required. Redirecting to login page.');
      return redirectToLogin();
  }
};

/**
 * Guest Guard (for public auth pages like forgot password, reset password, success pages)
 * 
 * This guard allows both authenticated and unauthenticated users to access these pages.
 * For authenticated users, it provides a better UX by allowing access to public pages.
 */
export const guestGuard: CanActivateFn = (route, state) => {
  const loginService = inject(LoginService);
  const router = inject(Router);

  // Allow access to all auth pages regardless of authentication status
  // This provides better UX for authenticated users who might want to:
  // - Change their password
  // - Access forgot password functionality
  // - View success messages
  return true;
};

/**
 * Login Guard (specifically for login page)
 *
 * Prevents authenticated users from sitting on /login. Routes them to the first
 * sidebar item their permissions allow, fetching /me first if permissions aren't
 * loaded yet (page refresh case). If the session has zero usable permissions —
 * e.g. an admin revoked them mid-session — drops the token and lets the login
 * page render so the user can see and act on the no-access state.
 */
export const loginGuard: CanActivateFn = (route, state) => {
  const loginService = inject(LoginService);
  const permissionService = inject(PermissionService);
  const userService = inject(UserService);
  const postLoginRedirect = inject(PostLoginRedirectService);
  const router = inject(Router);

  if (!loginService.isAuthenticated()) {
    return true;
  }

  const decide = (): boolean => {
    if (!permissionService.hasAnyAssignedPermission()) {
      loginService.logout();
      permissionService.clearPermissions();
      userService.clearCurrentUserCache();
      return true;
    }
    router.navigateByUrl(buildClientPath(router, postLoginRedirect.resolveLandingRoute()));
    return false;
  };

  // Permissions already initialized for this session — decide synchronously.
  if (permissionService.getAllPermissions() !== null) {
    return decide();
  }

  // Permissions not loaded (page refresh): fetch /me, then decide. The shared cache
  // makes this free for subsequent guards/resolvers in the same navigation.
  const stored = loginService.getUserInfo() as { nameIdentifier?: string } | null;
  if (!stored?.nameIdentifier) {
    return true;
  }

  return permissionService.ensurePermissions(stored.nameIdentifier).pipe(
    map(() => decide()),
    catchError(() => of(true))
  );
};

/**
 * OTP Verification Guard
 * 
 * This guard ensures that users can only access the OTP verification page
 * if they have successfully initiated the login process (credentials stored in sessionStorage).
 * If credentials are not found, redirects to login page.
 */
export const otpVerificationGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  
  try {
    // Check if login credentials exist in sessionStorage
    const storedCredentials = sessionStorage.getItem('login_credentials');
    const totpSetupCredentials = sessionStorage.getItem('totp_setup_credentials');
    
    // Allow access if either type of credentials exist
    if (storedCredentials || totpSetupCredentials) {
      return true;
    }
    
    // No credentials found, redirect to login
    console.warn('Login not initiated. Redirecting to login page.');
    router.navigate(buildClientRoute(router, ['/login']));
    return false;
  } catch (error) {
    console.error('Error checking login credentials:', error);
    router.navigate(buildClientRoute(router, ['/login']));
    return false;
  }
};